/**
 * HTML <-> Slate value serialization for the email editor.
 * Handles: p, h1-h3, br, strong/b, em/i, u, s, a, img, ul, ol, li, blockquote, table, div (with align/style).
 */
import type { Descendant } from 'slate';
import { Element as SlateElement, Text } from 'slate';
import type { CustomElement, CustomText } from './slate-types';

const DEFAULT_VALUE: Descendant[] = [{ type: 'paragraph', children: [{ text: '' }] }];

/** Inline void images must live in a block; bare root <img> tags are dropped by Slate on normalize. */
export function wrapOrphanImagesInParagraphs(html: string): string {
  if (!html || !/<img\b/i.test(html)) return html;
  if (typeof document === 'undefined') return html;
  const root = document.createElement('div');
  root.innerHTML = html.trim();
  let changed = false;
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    if ((node as Element).tagName.toLowerCase() !== 'img') continue;
    const p = document.createElement('p');
    root.insertBefore(p, node);
    p.appendChild(node);
    changed = true;
  }
  return changed ? root.innerHTML : html;
}

function imageElementFromImg(el: Element): CustomElement {
  const imgEl = el as HTMLElement;
  const url = el.getAttribute('src') || '';
  const alt = el.getAttribute('alt') || undefined;
  const width = imgEl.style?.width || el.getAttribute('width') || undefined;
  const height = imgEl.style?.height || el.getAttribute('height') || undefined;
  return {
    type: 'image',
    url,
    alt,
    width: width || undefined,
    height: height || undefined,
    children: [{ text: '' }],
  } as CustomElement;
}

function paragraphWithImage(el: Element): CustomElement {
  return {
    type: 'paragraph',
    children: [imageElementFromImg(el)],
  } as CustomElement;
}

function createText(text: string, marks: Partial<CustomText> = {}): CustomText {
  const t: CustomText = { text };
  if (marks.bold) t.bold = true;
  if (marks.italic) t.italic = true;
  if (marks.underline) t.underline = true;
  if (marks.strikethrough) t.strikethrough = true;
  if (marks.color) t.color = marks.color;
  if (marks.backgroundColor) t.backgroundColor = marks.backgroundColor;
  if (marks.fontFamily) t.fontFamily = marks.fontFamily;
  if (marks.fontSize) t.fontSize = marks.fontSize;
  return t;
}

function getAlign(el: Element): 'left' | 'center' | 'right' | 'justify' | undefined {
  const align = el.getAttribute('align') || (el as HTMLElement).style?.textAlign;
  if (align === 'center' || align === 'right' || align === 'justify' || align === 'left') return align;
  return undefined;
}

type InlineChild = CustomText | { type: 'link'; url: string; children: CustomText[] } | { type: 'image'; url: string; alt?: string; width?: string; height?: string };

const FONT_SIZE_ATTR_MAP: Record<string, string> = {
  '1': '10px',
  '2': '13px',
  '3': '16px',
  '4': '18px',
  '5': '24px',
  '6': '32px',
  '7': '48px',
};

/** Parse inline CSS declarations from a style attribute or CSSStyleDeclaration. */
function parseInlineStyleMarks(
  style: CSSStyleDeclaration | null | undefined,
  styleAttr?: string | null,
): Partial<CustomText> {
  const marks: Partial<CustomText> = {};
  const read = (key: string, cssKey: keyof CSSStyleDeclaration) => {
    const fromDecl = style?.[cssKey];
    const val = typeof fromDecl === 'string' ? fromDecl.trim() : '';
    if (val) (marks as Record<string, string>)[key] = val;
  };
  read('color', 'color');
  read('backgroundColor', 'backgroundColor');
  read('fontFamily', 'fontFamily');
  read('fontSize', 'fontSize');
  const background = style?.background?.trim() ?? '';
  if (background && !marks.backgroundColor) marks.backgroundColor = background;
  const weight = style?.fontWeight?.trim() ?? '';
  if (/^(bold|[6-9]00)$/i.test(weight)) marks.bold = true;

  if (!styleAttr?.trim()) return marks;
  for (const part of styleAttr.split(';')) {
    const colon = part.indexOf(':');
    if (colon === -1) continue;
    const key = part.slice(0, colon).trim().toLowerCase();
    const val = part.slice(colon + 1).trim();
    if (!val) continue;
    if (key === 'color') marks.color = val;
    else if (key === 'background-color' || key === 'background') marks.backgroundColor = val;
    else if (key === 'font-family') marks.fontFamily = val.replace(/^["']|["']$/g, '');
    else if (key === 'font-size') marks.fontSize = val;
    else if (key === 'font-weight' && /^(bold|[6-9]00)$/i.test(val)) marks.bold = true;
  }
  return marks;
}

function mergeMarks(base: Partial<CustomText>, extra: Partial<CustomText>): Partial<CustomText> {
  return { ...base, ...extra };
}

function inlineDescendantsFromNodes(nodes: NodeList | Node[]): Descendant[] {
  const inlines = parseInlineNodes(nodes);
  if (!inlines.length) return [createText('')];
  return inlines.map((n) => {
    if ('text' in n) return n;
    if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
    if (n.type === 'image') {
      return {
        type: 'image' as const,
        url: n.url,
        alt: n.alt,
        width: n.width,
        height: n.height,
        children: [{ text: '' }],
      };
    }
    return createText('');
  });
}

function parseInlineNodes(nodes: NodeList | Node[], marks: Partial<CustomText> = {}): InlineChild[] {
  const result: InlineChild[] = [];
  const walk = (list: NodeList | Node[], activeMarks: Partial<CustomText>) => {
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text) result.push(createText(text, activeMarks));
        continue;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      const styleAttr = el.getAttribute('style');
      const styleMarks = parseInlineStyleMarks((el as HTMLElement).style, styleAttr);
      let nextMarks = mergeMarks(activeMarks, styleMarks);
      if (tag === 'strong' || tag === 'b') nextMarks = mergeMarks(nextMarks, { bold: true });
      else if (tag === 'em' || tag === 'i') nextMarks = mergeMarks(nextMarks, { italic: true });
      else if (tag === 'u') nextMarks = mergeMarks(nextMarks, { underline: true });
      else if (tag === 's' || tag === 'strike') nextMarks = mergeMarks(nextMarks, { strikethrough: true });
      else if (tag === 'mark') {
        if (!nextMarks.backgroundColor) nextMarks.backgroundColor = '#ffff00';
        walk(el.childNodes, nextMarks);
        continue;
      }
      else if (tag === 'font') {
        const face = el.getAttribute('face');
        const color = el.getAttribute('color');
        const size = el.getAttribute('size');
        if (face) nextMarks.fontFamily = face;
        if (color) nextMarks.color = color;
        if (size && FONT_SIZE_ATTR_MAP[size]) nextMarks.fontSize = FONT_SIZE_ATTR_MAP[size];
        walk(el.childNodes, nextMarks);
        continue;
      }
      else if (tag === 'a') {
        const url = el.getAttribute('href') || '';
        const childNodes = parseInlineNodes(el.childNodes, nextMarks).filter((n): n is CustomText => 'text' in n);
        if (childNodes.length) result.push({ type: 'link', url, children: childNodes });
        else result.push({ type: 'link', url, children: [createText(url, nextMarks)] });
        continue;
      }
      else if (tag === 'img') {
        const imgEl = el as HTMLElement;
        const url = el.getAttribute('src') || '';
        const alt = el.getAttribute('alt') || undefined;
        const width = imgEl.style?.width || el.getAttribute('width') || undefined;
        const height = imgEl.style?.height || el.getAttribute('height') || undefined;
        result.push({ type: 'image', url, alt, width: width || undefined, height: height || undefined });
        continue;
      }
      if (tag === 'span') {
        const beforeLen = result.length;
        walk(el.childNodes, nextMarks);
        // Preserve marks on empty styled spans (compose defaults with no text yet).
        if (result.length === beforeLen && (nextMarks.fontFamily || nextMarks.fontSize || nextMarks.color || nextMarks.backgroundColor)) {
          result.push(createText('', nextMarks));
        }
        continue;
      }
      if (tag === 'br') result.push(createText('\n', activeMarks));
      else walk(el.childNodes, nextMarks);
    }
  };
  walk(nodes, marks);
  return result;
}

function parseBlock(el: Element): CustomElement | null {
  const tag = el.tagName.toLowerCase();
  const align = getAlign(el);

  if (tag === 'div' && el.getAttribute('data-slate-quoted-rich') === 'true') {
    const raw = el.getAttribute('data-html') || '';
    try {
      const innerHtml = raw ? decodeURIComponent(raw) : '';
      return {
        type: 'quoted-rich-html',
        html: innerHtml,
        children: [{ text: '' }],
      } as CustomElement;
    } catch {
      return { type: 'quoted-rich-html', html: '', children: [{ text: '' }] } as CustomElement;
    }
  }

  if (tag === 'pre' && (el.getAttribute('data-ums-preformat') === 'true' || el.classList.contains('ums-preformat'))) {
    return { type: 'preformat', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }

  if (tag === 'pre') {
    return {
      type: 'quoted-rich-html',
      html: el.outerHTML,
      children: [{ text: '' }],
    } as CustomElement;
  }

  if (tag === 'div' && el.hasAttribute('data-slate-raw-html')) {
    const raw = el.getAttribute('data-html') || '';
    const inline = el.hasAttribute('data-slate-inline-html');
    try {
      const html = raw ? decodeURIComponent(raw) : '';
      return {
        type: 'raw-html',
        html,
        ...(inline ? { inline: true } : {}),
        children: [{ text: '' }],
      } as CustomElement;
    } catch {
      return { type: 'raw-html', html: '', children: [{ text: '' }] } as CustomElement;
    }
  }

  if (tag === 'div' && el.getAttribute('data-ums-reply-header') === 'true') {
    const styleAttr = el.getAttribute('style') || '';
    const childBlocks = Array.from(el.children)
      .map((c) => parseBlock(c))
      .filter((n): n is CustomElement => n != null) as Descendant[];
    return {
      type: 'reply-header-box',
      style: styleAttr,
      children: childBlocks.length ? childBlocks : [{ type: 'paragraph', children: [{ text: '' }] }],
    } as CustomElement;
  }

  if (tag === 'div') {
    return { type: 'body-text', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'p') {
    return { type: 'paragraph', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'h1') {
    return { type: 'heading-one', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'h2') {
    return { type: 'heading-two', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'h3') {
    return { type: 'heading-three', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'h4') {
    return { type: 'heading-four', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'h5') {
    return { type: 'heading-five', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'h6') {
    return { type: 'heading-six', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'address') {
    return { type: 'address', align, children: inlineDescendantsFromNodes(el.childNodes) } as CustomElement;
  }
  if (tag === 'blockquote') {
    const children = Array.from(el.children).map((c) => parseBlock(c)).filter((n): n is CustomElement => n != null) as Descendant[];
    return { type: 'block-quote', children: children.length ? children : [{ type: 'paragraph', children: [{ text: '' }] }] } as CustomElement;
  }
  if (tag === 'ul') {
    const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => {
      const inlines = parseInlineNodes(li.childNodes);
      const itemChildren = inlines.length
        ? inlines.map((n) => {
            if ('text' in n) return n;
            if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
            if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
            return createText('');
          })
        : [createText('')];
      return { type: 'list-item', children: [{ type: 'paragraph', children: itemChildren }] } as CustomElement;
    });
    return { type: 'bulleted-list', children: items.length ? items : [{ type: 'list-item', children: [{ type: 'paragraph', children: [{ text: '' }] }] }] } as CustomElement;
  }
  if (tag === 'ol') {
    const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => {
      const inlines = parseInlineNodes(li.childNodes);
      const itemChildren = inlines.length
        ? inlines.map((n) => {
            if ('text' in n) return n;
            if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
            if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
            return createText('');
          })
        : [createText('')];
      return { type: 'list-item', children: [{ type: 'paragraph', children: itemChildren }] } as CustomElement;
    });
    return { type: 'numbered-list', children: items.length ? items : [{ type: 'list-item', children: [{ type: 'paragraph', children: [{ text: '' }] }] }] } as CustomElement;
  }
  if (tag === 'table') {
    const rows: CustomElement[] = [];
    const trs = el.querySelectorAll(':scope > tbody > tr, :scope > tr');
    trs.forEach((tr) => {
      const cells: CustomElement[] = [];
      tr.querySelectorAll(':scope > td, :scope > th').forEach((td) => {
        const colSpan = parseInt(td.getAttribute('colspan') || '1', 10) || 1;
        const rowSpan = parseInt(td.getAttribute('rowspan') || '1', 10) || 1;
        const inlines = parseInlineNodes(td.childNodes);
        const cellChildren = inlines.length
          ? inlines.map((n) => {
              if ('text' in n) return n;
              if (n.type === 'link') return { type: 'link' as const, url: n.url, children: n.children };
              if (n.type === 'image') return { type: 'image' as const, url: n.url, alt: n.alt, width: n.width, height: n.height, children: [{ text: '' }] };
              return createText('');
            })
          : [createText('')];
        const tdEl = td as HTMLElement;
        const cellWidth = tdEl.getAttribute('width') || tdEl.style?.width || undefined;
        const cellNode = { type: 'table-cell', colSpan, rowSpan, children: [{ type: 'paragraph', children: cellChildren }] } as CustomElement;
        if (cellWidth) (cellNode as { width?: string }).width = cellWidth;
        cells.push(cellNode);
      });
      const rowHeight = (tr as HTMLElement).getAttribute('height') || (tr as HTMLElement).style?.height || undefined;
      const rowNode = { type: 'table-row' as const, children: cells } as CustomElement;
      if (rowHeight) (rowNode as { height?: string }).height = rowHeight;
      if (cells.length) rows.push(rowNode);
    });
    const width = el.getAttribute('width') || (el as HTMLElement).style?.width || undefined;
    const height = el.getAttribute('height') || (el as HTMLElement).style?.height || undefined;
    const tableNode = { type: 'table' as const, children: rows.length ? rows : [{ type: 'table-row', children: [{ type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: '' }] }] }] }] } as CustomElement;
    if (width) (tableNode as { width?: string }).width = width;
    if (height) (tableNode as { height?: string }).height = height;
    return tableNode;
  }
  if (tag === 'img') {
    return paragraphWithImage(el);
  }
  return null;
}

function isBlockLevelTag(tag: string): boolean {
  return (
    tag === 'div' ||
    tag === 'p' ||
    tag === 'h1' ||
    tag === 'h2' ||
    tag === 'h3' ||
    tag === 'h4' ||
    tag === 'h5' ||
    tag === 'h6' ||
    tag === 'ul' ||
    tag === 'ol' ||
    tag === 'li' ||
    tag === 'table' ||
    tag === 'thead' ||
    tag === 'tbody' ||
    tag === 'tr' ||
    tag === 'td' ||
    tag === 'th' ||
    tag === 'blockquote' ||
    tag === 'pre' ||
    tag === 'address' ||
    tag === 'hr' ||
    tag === 'section' ||
    tag === 'article' ||
    tag === 'header' ||
    tag === 'footer'
  );
}

/** True when a wrapper div contains nested block elements (e.g. signature wrappers). */
function divHasBlockChildren(el: Element): boolean {
  for (let i = 0; i < el.children.length; i++) {
    if (isBlockLevelTag(el.children[i].tagName.toLowerCase())) return true;
  }
  return false;
}

/**
 * Deserialize HTML string to Slate Descendant[].
 * When we meet a div with block element children (e.g. email-signature wrapper), recurse so
 * inner blocks (p, img, etc.) are parsed and images are not lost.
 * Divs with only inline children (span/br/a) stay as Body Text blocks.
 */
export function htmlToSlate(html: string): Descendant[] {
  if (!html || !html.trim()) return DEFAULT_VALUE;
  const div = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (!div) return DEFAULT_VALUE;
  div.innerHTML = wrapOrphanImagesInParagraphs(html.trim());
  const blocks: Descendant[] = [];
  const walk = (parent: Element) => {
    for (let i = 0; i < parent.children.length; i++) {
      const el = parent.children[i];
      const tag = el.tagName.toLowerCase();
      if (tag === 'div' && el.getAttribute('data-ums-reply-header') === 'true') {
        const block = parseBlock(el);
        if (block) blocks.push(block);
        continue;
      }
      if (tag === 'div' && el.getAttribute('data-slate-quoted-rich') === 'true') {
        const block = parseBlock(el);
        if (block) blocks.push(block);
        continue;
      }
      if (tag === 'br') {
        blocks.push({ type: 'paragraph', children: [{ text: '\n' }] } as CustomElement);
        continue;
      }
      // Only unwrap wrapper divs that nest other blocks. Inline-only divs are Body Text.
      if (tag === 'div' && divHasBlockChildren(el)) {
        walk(el);
        continue;
      }
      const block = parseBlock(el);
      if (block) {
        blocks.push(block);
        continue;
      }
      if (el.children.length > 0) {
        walk(el);
      }
    }
  };
  walk(div);
  if (blocks.length === 0) return DEFAULT_VALUE;
  return blocks;
}

function serializeMarks(text: CustomText): string {
  let s = escapeHtml(text.text).replace(/\n/g, '<br />');
  if (text.bold) s = `<strong>${s}</strong>`;
  if (text.italic) s = `<em>${s}</em>`;
  if (text.underline) s = `<u>${s}</u>`;
  if (text.strikethrough) s = `<s>${s}</s>`;
  const spanParts: string[] = [];
  if (text.color) spanParts.push(`color:${text.color}`);
  if (text.backgroundColor) spanParts.push(`background-color:${text.backgroundColor}`);
  if (text.fontFamily) {
    // Prefer single quotes inside font stacks so HTML style="..." stays valid.
    const safeFont = text.fontFamily.replace(/"/g, "'");
    spanParts.push(`font-family:${safeFont}`);
  }
  if (text.fontSize) spanParts.push(`font-size:${text.fontSize}`);
  if (spanParts.length) s = `<span style="${escapeHtml(spanParts.join(';'))}">${s}</span>`;
  return s;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function serializeInlines(children: Descendant[]): string {
  return children.map((node) => {
    if (Text.isText(node)) return serializeMarks(node as CustomText);
    const n = node as CustomElement;
    if (n.type === 'link') return `<a href="${escapeHtml(n.url)}" target="_blank" rel="noopener noreferrer">${serializeInlines(n.children)}</a>`;
    if (n.type === 'image') {
      const img = n as { url: string; alt?: string; width?: string; height?: string };
      const styleParts: string[] = [];
      if (img.width) styleParts.push(`width: ${img.width}`);
      if (img.height) styleParts.push(`height: ${img.height}`);
      const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';
      return `<img src="${escapeHtml(img.url)}" alt="${escapeHtml(img.alt || '')}"${styleAttr} />`;
    }
    return serializeInlines(n.children);
  }).join('');
}

/** Empty marked spans are truthy HTML but collapse to 0 height in email clients — treat as blank. */
function isVisuallyEmptyInlineHtml(html: string): boolean {
  if (!html.trim()) return true;
  const text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<img\b[^>]*>/gi, 'IMG')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim();
  return text.length === 0;
}

function serializeBlockInner(children: Descendant[]): string {
  const raw = serializeInlines(children);
  return isVisuallyEmptyInlineHtml(raw) ? '<br />' : raw;
}

function serializeBlock(node: CustomElement): string {
  const children = node.children as Descendant[];
  const inner = () => serializeBlockInner(children);
  const align = (node as { align?: string }).align;
  const alignAttr = align ? ` align="${align}"` : '';

  switch (node.type) {
    case 'body-text':
      return `<div${alignAttr}>${inner()}</div>`;
    case 'paragraph':
      return `<p${alignAttr}>${inner()}</p>`;
    case 'heading-one':
      return `<h1${alignAttr}>${inner() || ' '}</h1>`;
    case 'heading-two':
      return `<h2${alignAttr}>${inner() || ' '}</h2>`;
    case 'heading-three':
      return `<h3${alignAttr}>${inner() || ' '}</h3>`;
    case 'heading-four':
      return `<h4${alignAttr}>${inner() || ' '}</h4>`;
    case 'heading-five':
      return `<h5${alignAttr}>${inner() || ' '}</h5>`;
    case 'heading-six':
      return `<h6${alignAttr}>${inner() || ' '}</h6>`;
    case 'address':
      return `<address${alignAttr}>${inner()}</address>`;
    case 'preformat':
      return `<pre data-ums-preformat="true" class="ums-preformat"${alignAttr}>${inner()}</pre>`;
    case 'block-quote':
      return `<blockquote class="email-quoted-block">${children.map((c) => serializeBlock(c as CustomElement)).join('')}</blockquote>`;
    case 'reply-header-box': {
      const box = node as { type: 'reply-header-box'; style?: string; children: Descendant[] };
      const styleAttr = box.style ? ` style="${escapeHtml(box.style)}"` : '';
      const innerHtml = box.children.map((c) => serializeBlock(c as CustomElement)).join('');
      return `<div data-ums-reply-header="true"${styleAttr}>${innerHtml}</div>`;
    }
    case 'quoted-rich-html': {
      const q = node as { type: 'quoted-rich-html'; html: string };
      const encoded = encodeURIComponent(q.html || '');
      return `<div data-slate-quoted-rich="true" data-html="${encoded}"></div>`;
    }
    case 'raw-html': {
      const rawNode = node as { type: 'raw-html'; html: string; inline?: boolean };
      const html = rawNode.html || '';
      const encoded = encodeURIComponent(html);
      const inlineAttr = rawNode.inline ? ' data-slate-inline-html="true"' : '';
      return `<div data-slate-raw-html="true"${inlineAttr} data-html="${encoded}"></div>`;
    }
    case 'list-item': {
      const itemInner = children.map((c) => (SlateElement.isElement(c) && (c as CustomElement).type === 'paragraph' ? serializeInlines((c as CustomElement).children) : '')).join('');
      return `<li>${isVisuallyEmptyInlineHtml(itemInner) ? '<br />' : itemInner}</li>`;
    }
    case 'bulleted-list':
      return `<ul>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</ul>`;
    case 'numbered-list':
      return `<ol>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</ol>`;
    case 'table': {
      const tbl = node as { width?: string; height?: string };
      const styleParts: string[] = [];
      if (tbl.width) styleParts.push(`width: ${tbl.width}`);
      if (tbl.height) styleParts.push(`height: ${tbl.height}`);
      const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';
      return `<table${styleAttr}><tbody>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</tbody></table>`;
    }
    case 'table-row': {
      const row = node as { height?: string };
      const rowStyle = row.height ? ` style="height: ${row.height}; min-height: ${row.height}"` : '';
      return `<tr${rowStyle}>${children.map((c) => serializeBlock(c as CustomElement)).join('')}</tr>`;
    }
    case 'table-cell': {
      const cell = node as { colSpan?: number; rowSpan?: number; width?: string };
      const cspan = cell.colSpan && cell.colSpan > 1 ? ` colspan="${cell.colSpan}"` : '';
      const rspan = cell.rowSpan && cell.rowSpan > 1 ? ` rowspan="${cell.rowSpan}"` : '';
      const styleParts: string[] = [];
      if (cell.width) styleParts.push(`width: ${cell.width}`);
      const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';
      const cellInner = children.map((c) => (SlateElement.isElement(c) && (c as CustomElement).type === 'paragraph' ? serializeInlines((c as CustomElement).children) : '')).join('');
      return `<td${cspan}${rspan}${styleAttr}>${isVisuallyEmptyInlineHtml(cellInner) ? '<br />' : cellInner}</td>`;
    }
    case 'image': {
      const imgNode = node as { url: string; alt?: string; width?: string; height?: string };
      const imgStyleParts: string[] = [];
      if (imgNode.width) imgStyleParts.push(`width: ${imgNode.width}`);
      if (imgNode.height) imgStyleParts.push(`height: ${imgNode.height}`);
      const imgStyleAttr = imgStyleParts.length ? ` style="${imgStyleParts.join('; ')}"` : '';
      const imgTag = `<img src="${escapeHtml(imgNode.url)}" alt="${escapeHtml(imgNode.alt || '')}"${imgStyleAttr} />`;
      return `<p>${imgTag}</p>`;
    }
    default:
      return `<p>${inner()}</p>`;
  }
}

/**
 * Serialize Slate Descendant[] to HTML string.
 */
export function slateToHtml(value: Descendant[]): string {
  if (!value || value.length === 0) return '<p><br /></p>';
  return value.map((node) => serializeBlock(node as CustomElement)).join('');
}

export { DEFAULT_VALUE };
